/**
 * Normalize sync-run status for display.
 * Historical stale closes were stored as `error` even when most products synced;
 * map those (and any error with successful creates/updates) to `partial`.
 */
export function resolveDisplaySyncStatus(run: {
  status?: string;
  productsCreated?: number;
  productsUpdated?: number;
  productsProcessed?: number;
  productsSkipped?: number;
  errors?: Array<string | { message?: string }>;
}): string {
  const status = run.status || 'unknown';
  if (status !== 'error') return status;

  const created = Number(run.productsCreated) || 0;
  const updated = Number(run.productsUpdated) || 0;
  if (created + updated > 0) return 'partial';

  const processed = Number(run.productsProcessed) || 0;
  const skipped = Number(run.productsSkipped) || 0;
  if (processed + skipped <= 0) return 'error';

  const hasStaleClose = (run.errors || []).some((entry) => {
    const message = typeof entry === 'string' ? entry : entry?.message || '';
    return /marked stale|closed as stale/i.test(message);
  });
  return hasStaleClose ? 'partial' : 'error';
}

/** Latest timestamp on a run — used to rank history so completedAt-only / missing startedAt rows still sort correctly. */
export function syncRunSortMs(run: {
  completedAt?: unknown;
  startedAt?: unknown;
  queuedAt?: unknown;
  createdAt?: unknown;
}, parseDate: (value: unknown) => Date | null): number {
  const times = [run.completedAt, run.startedAt, run.queuedAt, run.createdAt]
    .map((v) => parseDate(v)?.getTime())
    .filter((t): t is number => typeof t === 'number' && Number.isFinite(t) && t > 0);
  return times.length ? Math.max(...times) : 0;
}

/** Date shown for a run so the header Last Sync matches the top history row. */
export function syncRunDisplayValue(run: {
  status?: string;
  completedAt?: unknown;
  startedAt?: unknown;
  queuedAt?: unknown;
  createdAt?: unknown;
}): unknown {
  if (run.status === 'queued' || run.status === 'running') {
    return run.startedAt || run.queuedAt || run.createdAt || null;
  }
  return run.completedAt || run.startedAt || run.queuedAt || run.createdAt || null;
}

export type IntegrationSyncTrigger = 'scheduled' | 'manual' | 'auto_new_products';

export function resolveSyncRunTrigger(run: {
  trigger?: string;
  syncType?: string;
  forceFullSync?: boolean;
  executionMode?: string;
  syncImagesOverride?: boolean;
}): { trigger: IntegrationSyncTrigger; inferred: boolean } {
  if (run.trigger === 'scheduled' || run.trigger === 'manual' || run.trigger === 'auto_new_products') {
    return { trigger: run.trigger, inferred: false };
  }
  if (
    run.executionMode === 'direct' ||
    run.forceFullSync === true ||
    run.syncType === 'compare_update' ||
    typeof run.syncImagesOverride === 'boolean'
  ) {
    return { trigger: 'manual', inferred: true };
  }
  if (run.syncType === 'pricing_only') {
    return { trigger: 'scheduled', inferred: true };
  }
  return { trigger: 'manual', inferred: true };
}

export function syncTriggerLabel(
  trigger: IntegrationSyncTrigger,
  scheduleInterval?: string,
): string {
  if (trigger === 'scheduled') {
    const interval = scheduleInterval === 'hourly' || scheduleInterval === 'daily' || scheduleInterval === 'weekly'
      ? scheduleInterval.charAt(0).toUpperCase() + scheduleInterval.slice(1)
      : null;
    return interval ? `Scheduled · ${interval}` : 'Scheduled';
  }
  if (trigger === 'auto_new_products') return 'Auto-add';
  return 'Manual';
}

export function syncTriggerHint(
  trigger: IntegrationSyncTrigger,
  inferred: boolean,
  scheduleInterval?: string,
): string {
  const inferredNote = inferred ? ' Inferred from older run data (this was not stored at the time).' : '';
  if (trigger === 'scheduled') {
    const cadence = scheduleInterval === 'hourly' || scheduleInterval === 'daily' || scheduleInterval === 'weekly'
      ? `${scheduleInterval} auto-sync`
      : 'auto-sync';
    return `Queued by ${cadence}. Daily means at least 24 hours after the last finished sync, checked every 15 minutes — not a fixed clock time.${inferredNote}`;
  }
  if (trigger === 'auto_new_products') {
    return `Queued because new catalog products were added to this store's sync selection.${inferredNote}`;
  }
  return `Started from Sync Now (or another manual action).${inferredNote}`;
}

export function syncStatusLabel(status: string): string {
  switch (status) {
    case 'success':
      return 'Completed';
    case 'error':
      return 'Failed';
    case 'cancelled':
      return 'Cancelled';
    case 'partial':
      return 'Partial with errors';
    case 'running':
      return 'Running';
    case 'queued':
      return 'Queued';
    default:
      return status || 'Unknown';
  }
}
